import { useEffect, useRef } from "react";

/**
 * Custom hook to reload the page if it's been idle for more than 1 day
 * @param {number} maxIdleTime - Maximum idle time in milliseconds (default: 24 hours)
 */
const usePageReload = (maxIdleTime = 24 * 60 * 60 * 1000) => {
  const lastActivityRef = useRef(Date.now());

  useEffect(() => {
    // Update last activity time
    const updateActivity = () => {
      lastActivityRef.current = Date.now();
      localStorage.setItem("lastActivityTime", Date.now().toString());
    };

    // Check if page should reload on mount/visibility change
    const checkAndReload = () => {
      const storedTime = localStorage.getItem("lastActivityTime");
      const lastActivity = storedTime ? parseInt(storedTime, 10) : Date.now();
      const timeDiff = Date.now() - lastActivity;

      if (timeDiff >= maxIdleTime) {
        console.log("Page has been idle for more than 1 day. Reloading...");
        localStorage.removeItem("lastActivityTime");
        window.location.reload();
      } else {
        // Update activity if within time limit
        updateActivity();
      }
    };

    // Events to track user activity
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    // Handle page visibility change
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // User came back to the page
        checkAndReload();
      }
    };

    // Handle page focus
    const handleFocus = () => {
      checkAndReload();
    };

    // Initialize
    checkAndReload();

    // Add event listeners
    events.forEach((event) => {
      window.addEventListener(event, updateActivity);
    });
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    // Cleanup
    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, updateActivity);
      });
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [maxIdleTime]);
};

export default usePageReload;
